<template>
  <div class="notes-page">
    <section class="page-hero">
      <h1>我的笔记</h1>
      <p>划线、书签和笔记，都汇总在这里。</p>
    </section>

    <div v-if="loading" class="state"><nricon name="loading" /></div>

    <div v-else-if="loadError" class="state load-error">
      <nricon name="warning" />
      <h2>笔记载入失败</h2>
      <span class="hint">请检查网络后重试。</span>
      <button class="btn btn--sm" type="button" @click="load">重试</button>
    </div>

    <div v-else-if="!notes.length" class="state empty">
      <nricon name="notebook-1" />
      <h2>还没有划线或书签</h2>
      <span class="hint">阅读时选中文字划线、或点顶栏星标加书签，都会汇总到这里</span>
    </div>

    <main v-else class="notes-body">
      <section class="notes-filters" aria-label="笔记筛选">
        <div class="filter-line"><span>媒介</span><div role="group" aria-label="按媒介筛选">
          <button v-for="option in mediaOptions" :key="option.value" type="button" :class="{ active: mediaFilter === option.value }" :aria-pressed="mediaFilter === option.value" @click="mediaFilter = option.value">{{ option.label }}</button>
        </div></div>
        <div class="filter-line"><span>类型</span><div role="group" aria-label="按笔记类型筛选">
          <button v-for="option in noteOptions" :key="option.value" type="button" :class="{ active: typeFilter === option.value }" :aria-pressed="typeFilter === option.value" @click="typeFilter = option.value">{{ option.label }}</button>
        </div></div>
        <label class="group-mode"><span>排列</span><select class="nr-select" v-model="groupMode"><option value="book">按内容分组</option><option value="time">按时间排序</option></select></label>
      </section>
      <div v-if="!filteredNotes.length" class="filtered-empty">没有符合条件的笔记。<button type="button" @click="clearFilters">清除筛选</button></div>
      <template v-else>
      <div class="summary">共 {{ filteredNotes.length }} 条笔记 · {{ groups.length }} 本书</div>
      <section v-if="groupMode === 'book'" v-for="g in groups" :key="g.bookId" class="book-group">
        <button v-if="g.bookAvailable" class="group-head" @click="goToBook(g.bookId)">
          <nricon name="reading" />
          <span class="g-title">{{ g.bookTitle }}</span>
          <span class="g-count">{{ g.items.length }} 条</span>
        </button>
        <div v-else class="group-head unavailable-head"><nricon name="lock" /><span class="g-title">不可访问的内容</span><span class="g-count">{{ g.items.length }} 条</span></div>
        <ul class="note-list">
          <li v-for="n in g.items" :key="n.id" class="note-item" :class="{ unavailable: !n.bookAvailable }" :role="n.bookAvailable ? 'button' : null" :tabindex="n.bookAvailable ? 0 : null" @click="goToChapter(g.bookId, n)" @keyup.enter="goToChapter(g.bookId, n)">
            <div class="n-main">
              <div v-if="n.note && n.bookAvailable" class="n-note">{{ n.note }}</div>
              <div v-else-if="!n.bookAvailable" class="n-note n-empty">来源内容当前不可访问</div>
              <div v-else class="n-note n-empty">（书签）</div>
              <div class="n-meta">
                <span v-if="noteLocation(n)" class="n-chapter">{{ noteLocation(n) }}</span>
                <span class="n-time">{{ formatTime(n.createdAt) }}</span>
              </div>
            </div>
            <button class="n-del" title="删除" aria-label="删除笔记" @click.stop="remove(n)"><nricon name="delete" /></button>
          </li>
        </ul>
      </section>
      <ul v-else class="note-list time-list">
        <li v-for="n in filteredNotes" :key="n.id" class="note-item" :class="{ unavailable: !n.bookAvailable }" :role="n.bookAvailable ? 'button' : null" :tabindex="n.bookAvailable ? 0 : null" @click="goToChapter(n.bookId, n)" @keyup.enter="goToChapter(n.bookId, n)">
          <div class="n-main"><div class="n-source">{{ n.bookAvailable ? (n.bookTitle || '未知内容') : '不可访问的内容' }} · {{ mediaLabel(n) }}</div><div v-if="n.note && n.bookAvailable" class="n-note">{{ n.note }}</div><div v-else-if="!n.bookAvailable" class="n-note n-empty">来源内容当前不可访问</div><div v-else class="n-note n-empty">（书签）</div><div class="n-meta"><span v-if="noteLocation(n)" class="n-chapter">{{ noteLocation(n) }}</span><span class="n-time">{{ formatTime(n.createdAt) }}</span></div></div>
          <button class="n-del" type="button" title="删除" aria-label="删除笔记" @click.stop="remove(n)"><nricon name="delete" /></button>
        </li>
      </ul>
      </template>
    </main>
  </div>
</template>

<script>
import api from '@/api'
import { cleanTitle } from '@/utils/title'

export default {
  name: 'NotesPage',
  data() {
    return {
      loading: false,
      loadError: false,
      notes: [],
      mediaFilter: '',
      typeFilter: '',
      groupMode: 'book',
      mediaOptions: [
        { value: '', label: '全部' }, { value: 'TEXT', label: '电子书' },
        { value: 'PDF', label: 'PDF' }, { value: 'AUDIO', label: '有声书' }
      ],
      noteOptions: [
        { value: '', label: '全部' }, { value: 'HIGHLIGHT', label: '划线' },
        { value: 'BOOKMARK', label: '书签' }, { value: 'ANNOTATION', label: '批注' },
        { value: 'TIMESTAMP', label: '时间点' }
      ]
    }
  },
  computed: {
    filteredNotes() {
      return this.notes.filter(note => (!this.mediaFilter || String(note.mediaType || 'TEXT').toUpperCase() === this.mediaFilter)
        && (!this.typeFilter || String(note.noteType || (note.note ? 'HIGHLIGHT' : 'BOOKMARK')).toUpperCase() === this.typeFilter))
        .slice().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    },
    // 按书分组，保持原有（时间倒序）顺序
    groups() {
      const map = new Map()
      for (const n of this.filteredNotes) {
        const key = n.bookId
        if (!map.has(key)) {
          map.set(key, {
            bookId: n.bookId,
            bookTitle: n.bookAvailable ? cleanTitle(n.bookTitle || '未知内容') : '不可访问的内容',
            bookAvailable: n.bookAvailable !== false,
            items: []
          })
        }
        map.get(key).items.push(n)
      }
      return Array.from(map.values())
    }
  },
  created() {
    if (!this.$store.state.user) {
      this.$router.replace('/login')
      return
    }
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.loadError = false
      try {
        const { data } = await api.bookmark.listAll()
        this.notes = data || []
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    goToBook(bookId) {
      if (!this.notes.some(note => String(note.bookId) === String(bookId) && note.bookAvailable !== false)) return
      this.$router.push({ name: 'BookDetail', params: { id: String(bookId) } })
    },
    goToChapter(bookId, note) {
      if (note.bookAvailable === false) return
      const query = {}
      if (note.chapterId) query.chapterId = note.chapterId
      if (note.mediaType === 'PDF' && note.pageIndex != null) query.page = String(note.pageIndex + 1)
      if (note.mediaType === 'AUDIO') {
        query.time = String(Math.floor(note.positionSeconds || 0))
        if (note.trackId) query.trackId = String(note.trackId)
      }
      this.$router.push(note.mediaType === 'PDF'
        ? { name: 'PdfReader', params: { bookId: String(bookId) }, query }
        : note.mediaType === 'AUDIO'
          ? { name: 'Listen', params: { bookId: String(bookId) }, query }
          : { name: 'Reader', params: { bookId: String(bookId) }, query })
    },
    noteLocation(note) {
      if (note.mediaType === 'PDF' && note.pageIndex != null) return `第 ${note.pageIndex + 1} 页`
      if (note.mediaType === 'AUDIO' && note.positionSeconds != null) return this.formatDuration(note.positionSeconds)
      return note.chapterTitle || ''
    },
    mediaLabel(note) { return ({ PDF: 'PDF', AUDIO: '有声书' })[note.mediaType] || '电子书' },
    clearFilters() { this.mediaFilter = ''; this.typeFilter = '' },
    formatDuration(value) {
      const seconds = Math.max(0, Math.floor(Number(value) || 0))
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const remainder = seconds % 60
      return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}` : `${minutes}:${String(remainder).padStart(2, '0')}`
    },
    async remove(note) {
      try {
        await this.$confirm('确定删除这条笔记吗？', '提示', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await api.bookmark.remove(note.id)
        this.notes = this.notes.filter(n => n.id !== note.id)
        this.$message.success('已删除')
      } catch (e) {
        if (e !== 'cancel') this.$message.error('删除失败')
      }
    },
    formatTime(t) {
      if (!t) return ''
      const d = new Date(t)
      if (isNaN(d.getTime())) return ''
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
}
</script>

<style lang="scss" scoped>
.notes-page {
  min-height: 100vh;
  background: var(--nr-bg);
  padding-bottom: 40px;
}

.page-top {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: var(--nr-surface);
  border-bottom: 1px solid var(--nr-border);

  h2 { margin: 0; font-size: 17px; font-weight: 500; color: var(--nr-text); }
  .spacer { flex: 1; }
  .back { flex-shrink: 0; }
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 90px 20px;
  color: var(--nr-text-3);
  i { font-size: 40px; }
  &.empty {
    p { margin: 14px 0 6px; font-size: 15px; color: var(--nr-text-2); }
    .hint { font-size: 13px; color: var(--nr-text-3); }
  }
}

.notes-body {
  max-width: 800px;
  margin: 0 auto;
  padding: 18px 20px 0;
}

.notes-filters { display: grid; gap: 9px; margin-bottom: 20px; padding: 15px 16px; border: 1px solid var(--nr-border); border-radius: 13px; background: var(--nr-surface); }
.filter-line { display: flex; align-items: center; gap: 12px; > span { width: 42px; flex: 0 0 42px; color: var(--nr-text-3); font-size: 11px; font-weight: 600; } > div { display: flex; flex-wrap: wrap; gap: 5px; } button { min-height: 32px; padding: 0 10px; color: var(--nr-text-2); border: 1px solid transparent; border-radius: 8px; background: transparent; font-size: 11px; cursor: pointer; &.active { color: var(--nr-accent-text); border-color: color-mix(in srgb, var(--nr-accent) 22%, transparent); background: var(--nr-accent-bg); font-weight: 650; } &:focus-visible { outline: 2px solid var(--nr-focus); outline-offset: 2px; } } }
.group-mode { display: flex; align-items: center; gap: 12px; padding-top: 9px; border-top: 1px solid var(--nr-border); > span { width: 42px; flex: 0 0 42px; color: var(--nr-text-3); font-size: 11px; font-weight: 600; } .nr-select { min-height: 33px; font-size: 11px; } }
.filtered-empty { padding: 28px; color: var(--nr-text-2); border: 1px dashed var(--nr-border-strong); border-radius: 12px; text-align: center; font-size: 13px; button { margin-left: 7px; color: var(--nr-accent-text); border: 0; background: transparent; cursor: pointer; } }
.n-source { margin-bottom: 7px; color: var(--nr-accent-text); font-size: 10px; font-weight: 650; }
.note-item.unavailable { cursor: default; }
.summary {
  font-size: 13px;
  color: var(--nr-text-3);
  margin-bottom: 14px;
}

.book-group {
  margin-bottom: 22px;

  .group-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    cursor: pointer;
    color: var(--nr-text);
    i { color: var(--nr-accent); }
    .g-title { font-size: 15px; font-weight: 500; }
    .g-count { margin-left: auto; font-size: 12px; color: var(--nr-text-3); }
    &:hover .g-title { color: var(--nr-accent); }
  }
}

.note-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.note-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: var(--nr-surface);
  border: 1px solid var(--nr-border);
  border-left: 3px solid var(--nr-accent);
  border-radius: var(--nr-radius);
  cursor: pointer;
  transition: box-shadow .15s;
  &:hover { box-shadow: var(--nr-shadow-hover); }

  .n-main { flex: 1; min-width: 0; }
  .n-note {
    font-size: 14px;
    color: var(--nr-text);
    line-height: 1.6;
    word-break: break-word;
    &.n-empty { color: var(--nr-text-3); font-style: italic; }
  }
  .n-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 6px;
    font-size: 12px;
    color: var(--nr-text-3);
  }
  .n-chapter {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 60%;
  }

  .n-del {
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: var(--nr-text-3);
    cursor: pointer;
    padding: 2px 4px;
    font-size: 15px;
    &:hover { color: #f56c6c; }
  }
}

@media screen and (max-width: 768px) {
  .page-top { padding: 10px 14px; }
  .notes-body { padding: 14px 14px 0; }
}
</style>

<style lang="scss" scoped>
.notes-page { padding-bottom: 72px; background: transparent; }

.page-top {
  padding: 0;
  border-bottom: 1px solid color-mix(in srgb, var(--nr-border) 80%, transparent);
  background: color-mix(in srgb, var(--nr-surface) 90%, transparent);
  backdrop-filter: blur(18px);
}

.page-top-inner {
  display: flex;
  width: min(100% - 40px, 960px);
  min-height: 68px;
  align-items: center;
  justify-content: space-between;
  margin-inline: auto;
}

.back,
.mini-brand {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: 7px;
  padding: 0;
  color: var(--nr-text-2);
  border: 0;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.back:hover { color: var(--nr-accent-text); }
.mini-brand { color: var(--nr-text); font-family: var(--nr-font-display); font-size: 18px; }
.mini-brand i { display: grid; width: 31px; height: 31px; place-items: center; color: var(--nr-on-accent); border-radius: 50% 50% 50% 10px; background: var(--nr-accent); font-family: var(--nr-font-body); font-size: 16px; }

.page-hero {
  width: min(100% - 40px, 960px);
  margin-inline: auto;
  padding: 34px 0 22px;
  border-bottom: 1px solid var(--nr-border);

  h1 { margin: 0; font-family: var(--nr-font-display); font-size: clamp(30px, 4vw, 38px); font-weight: 600; line-height: 1.15; letter-spacing: -.02em; }
  p { margin: 10px 0 0; color: var(--nr-text-2); font-size: 14px; }
}

.notes-body { max-width: 960px; padding: 30px 0 0; }
.summary {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
  color: var(--nr-text-3);
  font-size: 13px;
  &::after { content: ''; height: 1px; flex: 1; background: var(--nr-border); }
}

.book-group { margin-bottom: 34px; }
.book-group .group-head {
  display: flex;
  width: 100%;
  min-height: 48px;
  align-items: center;
  gap: 10px;
  padding: 0 2px;
  color: var(--nr-text);
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;

  > i { display: grid; width: 32px; height: 32px; place-items: center; color: var(--nr-accent-text); border-radius: 50%; background: var(--nr-accent-bg); }
  .g-title { font-size: 17px; font-weight: 600; }
  .g-count { margin-left: auto; padding: 3px 8px; border-radius: 999px; background: var(--nr-surface-2); color: var(--nr-text-3); font-size: 11px; }
}

.note-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.time-list { padding: 0; list-style: none; }
.note-item {
  min-height: 116px;
  padding: 18px 16px 15px;
  border-left-width: 1px;
  border-radius: var(--nr-radius);
  box-shadow: none;
  transition: border-color 180ms ease, box-shadow 180ms ease;

  &:hover { border-color: var(--nr-border-strong); box-shadow: var(--nr-shadow); }
  &:focus-visible { outline: 2px solid var(--nr-accent); outline-offset: 3px; }
  .n-note { font-size: 14px; line-height: 1.7; }
  .n-meta { margin-top: 13px; padding-top: 10px; border-top: 1px solid var(--nr-border); color: var(--nr-text-3); font-size: 11px; }
  .n-del { display: grid; width: 34px; height: 34px; place-items: center; border-radius: 50%; }
  .n-del:hover { color: var(--nr-danger); background: var(--nr-danger-bg); }
}

.state { width: min(100% - 40px, 960px); min-height: 300px; margin-inline: auto; padding: 70px 20px; }
.state.empty {
  margin-top: 30px;
  border: 1px dashed var(--nr-border-strong);
  border-radius: var(--nr-radius-lg);
  i { display: grid; width: 54px; height: 54px; place-items: center; color: var(--nr-accent-text); border-radius: 50%; background: var(--nr-accent-bg); font-size: 23px; }
  h2 { margin: 16px 0 7px; color: var(--nr-text); font-size: 19px; font-weight: 600; }
}

@media (max-width: 768px) {
  .notes-page { padding-bottom: 44px; }
  .notes-filters { padding: 12px; }
  .filter-line { align-items: flex-start; }
  .filter-line > div { overflow-x: auto; flex-wrap: nowrap; max-width: calc(100vw - 100px); }
  .filter-line button { flex: 0 0 auto; }
  .note-list { grid-template-columns: 1fr; }
  .page-top-inner,
  .page-hero,
  .notes-body,
  .state { width: min(100% - 28px, 960px); }
  .page-top-inner { min-height: 62px; }
  .mini-brand span { display: none; }
  .page-hero { padding: 26px 0 20px; }
  .page-hero h1 { font-size: 32px; }
  .notes-body { padding-top: 24px; }
  .note-list { grid-template-columns: 1fr; }
  .note-item { min-height: 118px; }
  .note-item .n-del { width: 44px; height: 44px; }
}
</style>
